import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import type { ButtonSize } from "ui/buttons/Button";
import Button from "ui/buttons/Button";
import type { PopoverPosition } from "ui/components/PopoverMenu";
import { StatelessPopoverMenu } from "ui/components/PopoverMenu";
import cx from "ui/cx";
import IconMiniChevronDown from "ui/icons/IconMiniChevronDown";
import styles from "./SplitButton.module.scss";

interface SplitButtonProps {
  innerEl: ReactNode | string;
  onClick: () => void;
  menuItems: ReactNode[];
  size: ButtonSize;
  disabled?: boolean;
  title?: string;
  className?: string;
  menuPosition?: PopoverPosition;
}

const chevronWidths: Partial<{ [btnSize in ButtonSize]: number }> = {
  small: 9,
  large: 12,
};

const SplitButton = ({
  innerEl,
  onClick,
  menuItems,
  size = "large",
  disabled = false,
  title,
  className,
  menuPosition = "bottom",
}: SplitButtonProps) => {
  const splitButton = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleWindowClick = (event: any) => {
    if (!splitButton.current?.contains(event.target)) {
      setIsOpen(false);
    }
  };

  const handleButtonClick = () => {
    if (isOpen) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    window.document.addEventListener("click", handleWindowClick);
    return () => {
      window.document.removeEventListener("click", handleWindowClick);
    };
  }, []);

  const chevronWidth = chevronWidths[size] ?? 12;

  return (
    <div
      ref={splitButton}
      className={cx(styles.splitButtonWrapper, className)}
      onClick={handleButtonClick}
      title={disabled ? title : undefined}
    >
      <Button
        className={cx(styles.splitButtonText, "split-button-text")}
        btnSize={size}
        onClick={onClick}
        disabled={disabled}
      >
        {innerEl}
      </Button>
      <Button
        className={styles.splitButtonArrow}
        btnSize={size}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        disabled={disabled}
      >
        <IconMiniChevronDown width={chevronWidth} height={(2 * chevronWidth) / 3} />
      </Button>
      {isOpen && <StatelessPopoverMenu menuItems={menuItems} position={menuPosition} />}
    </div>
  );
};

export default SplitButton;
